import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  BookOpen, 
  Users, 
  Calendar, 
  Award,
  MessageCircle,
  TrendingUp,
  TrendingDown,
  Heart,
  Lightbulb,
  GraduationCap,
  Globe,
  Sparkles
} from "lucide-react";

const OpenStudyHistory = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "OpenStudy History: The Rise and Fall of Peer Learning Pioneer",
    "description": "Explore the complete history of OpenStudy, the pioneering peer-to-peer learning platform that connected millions of students worldwide before its acquisition by Brainly.",
    "image": "https://csw.live/og-openstudy-history.png",
    "author": {
      "@type": "Organization",
      "name": "CSW - Pair Study & Work",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Pair Study & Work",
      "url": "https://csw.live",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo.png"
      }
    },
    "datePublished": "2024-01-15",
    "dateModified": "2024-01-15",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/openstudy/history/"
    }
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "OpenStudy",
    "alternateName": "OpenStudy.com",
    "description": "OpenStudy was a pioneering social learning network that enabled peer-to-peer academic help and collaborative study, serving over 50 million learners worldwide.",
    "foundingDate": "2007",
    "dissolutionDate": "2015",
    "founder": [
      {
        "@type": "Person",
        "name": "Dr. Ashwin Ram",
        "jobTitle": "Co-founder",
        "affiliation": "Georgia Tech"
      }
    ],
    "areaServed": "Worldwide",
    "knowsAbout": ["Peer Learning", "Academic Help", "Social Learning", "Homework Assistance", "Collaborative Education"]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>OpenStudy History: The Rise and Fall of Peer Learning Pioneer | CSW</title>
        <meta name="description" content="Explore the complete history of OpenStudy, the pioneering peer-to-peer learning platform that connected millions of students worldwide before its acquisition by Brainly in 2015." />
        <meta name="keywords" content="OpenStudy history, OpenStudy shutdown, peer learning platform, social learning network, Brainly acquisition, homework help history" />
        <link rel="canonical" href="https://csw.live/openstudy/history/" />
        <meta property="og:title" content="OpenStudy History: The Rise and Fall of Peer Learning Pioneer" />
        <meta property="og:description" content="Explore the complete history of OpenStudy, the pioneering peer-to-peer learning platform that connected millions of students worldwide." />
        <meta property="og:url" content="https://csw.live/openstudy/history/" />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OpenStudy History: The Rise and Fall of Peer Learning Pioneer" />
        <meta name="twitter:description" content="The complete story of OpenStudy's journey from pioneering social learning to Brainly acquisition." />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(orgSchema)}
        </script>
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            <BookOpen className="w-3 h-3 mr-1" />
            EdTech History
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
            OpenStudy: Pioneering Peer Learning, Then Fading Away
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            The story of how a Georgia Tech-founded platform helped 50 million learners before becoming part of <a href="https://brainly.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Brainly</a>'s ecosystem.
          </p>
        </div>

        {/* Timeline Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-card rounded-xl p-4 text-center border">
            <Calendar className="w-6 h-6 mx-auto mb-2 text-primary" />
            <div className="text-2xl font-bold">2007</div>
            <div className="text-sm text-muted-foreground">Founded</div>
          </div>
          <div className="bg-card rounded-xl p-4 text-center border">
            <Users className="w-6 h-6 mx-auto mb-2 text-secondary" />
            <div className="text-2xl font-bold">50M+</div>
            <div className="text-sm text-muted-foreground">Learners Helped</div>
          </div>
          <div className="bg-card rounded-xl p-4 text-center border">
            <Globe className="w-6 h-6 mx-auto mb-2 text-primary" />
            <div className="text-2xl font-bold">Global</div>
            <div className="text-sm text-muted-foreground">Reach</div>
          </div>
          <div className="bg-card rounded-xl p-4 text-center border">
            <TrendingDown className="w-6 h-6 mx-auto mb-2 text-destructive" />
            <div className="text-2xl font-bold">2015</div>
            <div className="text-sm text-muted-foreground">Acquired</div>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Founding Vision Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Lightbulb className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Founding Vision and Platform Features</h2>
          </div>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              OpenStudy launched in <strong>2007</strong> as one of the early "social learning" networks on the web. Founded by a team including <a href="https://www.cc.gatech.edu/people/ashwin-ram" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Dr. Ashwin Ram</a> from <a href="https://www.gatech.edu" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Georgia Tech</a> and an <a href="https://www.emory.edu" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Emory University</a> dean, OpenStudy set out with a bold mission: to create a massive online study group where students anywhere could ask questions and get help from peers in real time.
            </p>
            <p>
              The platform's mascot – a wise owl – symbolized its goal of collaborative knowledge-sharing. OpenStudy's core features centered on Q&A forums organized by subject. High school and college learners would post homework questions (math problems, chemistry concepts, etc.), and others in the community would respond with guidance or solutions.
            </p>
            <p>
              This peer-to-peer model, combined with game elements like points and achievement badges for helpful answers, fostered an engaged community. By integrating chat and study groups, OpenStudy felt less like a static forum and more like a dynamic study hall. At its peak, OpenStudy boasted helping over <strong>50 million learners worldwide</strong>, reflecting its popularity as a free academic help resource.
            </p>
            <p>
              The appeal was clear: students could get quick help at any hour while also teaching others, reinforcing their own knowledge. In an era before pervasive social media study groups, OpenStudy was a trailblazer in connecting student minds across borders. The platform even received support from the <a href="https://www.nsf.gov" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">U.S. National Science Foundation</a>.
            </p>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Trajectory Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <TrendingUp className="w-6 h-6 text-secondary" />
            </div>
            <h2 className="text-2xl font-bold">Trajectory and Reasons for Shutdown</h2>
          </div>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              OpenStudy's trajectory saw early success and recognition, but it faced challenges sustaining itself long-term. In 2012, OpenStudy was integrated into online courses and <a href="https://www.mooc.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">MOOCs</a>, expanding its user base. However, monetization remained elusive – the platform was free and grant-funded.
            </p>
            <p>
              By 2015, OpenStudy's growth had plateaued, and maintaining the service at scale became difficult. In <strong>June 2015</strong>, the OpenStudy website was quietly closed, and an acquisition was in the works. The following year, <a href="https://brainly.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Brainly</a> (a global homework help company) acquired OpenStudy's assets and team. This effectively marked OpenStudy's end as an independent service.
            </p>
            <p>
              The official line was that OpenStudy "faced sustainability challenges and community support issues," leading to its closure. In practice, the vibrant user community had gradually migrated to larger platforms or lost engagement. After the Brainly acquisition, OpenStudy's site was eventually taken offline completely.
            </p>
            <p>
              The shutdown stemmed from a combination of financial strain, competition from newer Q&A platforms, and the shift of its users to Brainly's ecosystem. The rise of alternative solutions like <a href="https://www.chegg.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Chegg</a>'s Q&A or <a href="https://www.reddit.com/r/HomeworkHelp/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Reddit homework help forums</a> likely siphoned users as well. By mid-2015, OpenStudy was read-only, and soon after, it vanished from the web.
            </p>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Community Reactions Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Heart className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Community Reactions and Remnants</h2>
          </div>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              The closing of OpenStudy was met with disappointment by its loyal users, some of whom had relied on it daily for academic support. Students on <a href="https://www.reddit.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Reddit</a> recall clicking old OpenStudy links only to find them dead – a stark end for a site that had felt like a "lifeline" during tough homework nights.
            </p>
            <p>
              There was a sense of nostalgia for the friendly study partners and altruistic helpers that defined the community. However, the transition to Brainly softened the blow for many, since Brainly offered a similar Q&A service. Indeed, Brainly assured that OpenStudy's mission would live on within its larger network.
            </p>
            <p>
              In terms of what remains of OpenStudy: the original OpenStudy.com is defunct, but its DNA survives. <a href="https://brainly.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Brainly</a> integrated OpenStudy's user base and some design elements (notably, a points-based system to gamify helping) into its platform. The OpenStudy owl mascot was retired, and user profiles did not carry over, meaning personal contributions and badges were lost.
            </p>
            <p>
              Yet, one can argue OpenStudy's spirit persists wherever students help each other online. A small consolation is that OpenStudy ended not with an abrupt shutdown, but a merger of sorts – its community was given another home. As one education blogger noted, "OpenStudy functioned as an innovative learning tool in its time but ultimately faced challenges that led to its closure."
            </p>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Key Features Box */}
        <section className="mb-12">
          <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 border">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary" />
              What Made OpenStudy Special
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="p-2 bg-primary/10 rounded-lg h-fit">
                  <MessageCircle className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Real-Time Q&A</h3>
                  <p className="text-sm text-muted-foreground">Instant peer responses to homework questions at any hour</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="p-2 bg-secondary/10 rounded-lg h-fit">
                  <Award className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Gamified Learning</h3>
                  <p className="text-sm text-muted-foreground">Points and badges rewarded helpful contributors</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="p-2 bg-primary/10 rounded-lg h-fit">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Peer-to-Peer Model</h3>
                  <p className="text-sm text-muted-foreground">Everyone could be both student and tutor</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="p-2 bg-secondary/10 rounded-lg h-fit">
                  <GraduationCap className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">MOOC Integration</h3>
                  <p className="text-sm text-muted-foreground">Partnered with online courses to expand reach</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-2xl p-8 border">
          <h2 className="text-2xl font-bold mb-4">Continue the Peer Learning Legacy</h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            OpenStudy's mission lives on. Join CSW for real-time collaborative study sessions with peers worldwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/openstudy/" 
              className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Explore OpenStudy Alternative
            </a>
            <a 
              href="/study-with-me/" 
              className="inline-flex items-center justify-center px-6 py-3 bg-secondary/10 text-secondary-foreground rounded-lg font-medium hover:bg-secondary/20 transition-colors border"
            >
              Start Studying Now
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OpenStudyHistory;
